import { useState } from "react";
import {
  Activity,
  CheckCircle2,
  Clock,
  AlertTriangle,
  RefreshCw,
  Bot,
  List,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { TopBarSection } from "@/components/shared/SecaoBarraSuperior";
import { NumberKpiCard } from "@/components/shared/NumberKpiCard";
import { PageTabs } from "@/components/shared/PageTabs";

import { ProcessamentoIAListTable } from "../../components/processamentos-ia/ProcessamentoIAListTable";
import { ProcessamentoIAEstatisticasPanel } from "../../components/processamentos-ia/ProcessamentoIAEstatisticasPanel";
import { useProcessamentoIAKpis } from "@/hooks/processamento-ia/use-processamento-ia-kpis";
import { cn } from "@/lib/utils";

export function ProcessamentoIAAdminPage() {
  const { resumo, isLoading, refetch } = useProcessamentoIAKpis();

  const [isSyncing, setIsSyncing] = useState(false);

  const handleRefresh = async () => {
    setIsSyncing(true);
    try {
      await refetch();
      toast.success("Painel de IA atualizado", {
        icon: <RefreshCw className="h-4 w-4 text-primary" />,
      });
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="flex flex-col space-y-6 pb-10 duration-500 animate-in fade-in">
      <TopBarSection
        entity="IA"
        title="Gestão de Processamentos de IA"
        extraActions={[
          <Button
            key="sync-action"
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading || isSyncing}
            className="gap-2 border-primary/20 transition-all hover:bg-primary/5"
          >
            <RefreshCw className={cn("h-4 w-4", (isLoading || isSyncing) && "animate-spin")} />
            <span className="hidden sm:inline">Sincronizar</span>
          </Button>,
        ]}
      />

      {/* Grid de KPIs - 4 colunas em telas grandes, 2 em tablets */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <NumberKpiCard
          title="Total de Processamentos"
          value={resumo.total}
          icon={Activity}
          variant="blue"
          loading={isLoading}
        />

        <NumberKpiCard
          title="Concluídos"
          value={resumo.sucesso}
          icon={CheckCircle2}
          variant="emerald"
          loading={isLoading}
        />

        <NumberKpiCard
          title="Pendentes"
          value={resumo.pendente}
          icon={Clock}
          variant="amber"
          loading={isLoading}
        />

        <NumberKpiCard
          title="Falhas"
          value={resumo.falha}
          icon={AlertTriangle}
          variant="destructive"
          loading={isLoading}
        />
      </div>

      <PageTabs
        paramKey="tab"
        tabs={[
          {
            value: "historico",
            label: "Histórico",
            icon: List,
            content: (
              <div className="flex flex-col space-y-4 rounded-xl border border-border bg-card p-6 shadow-sm">
                {/* Um pequeno header interno para dar contexto à tabela */}
                <div className="flex items-center gap-2 border-b border-border/50 pb-4">
                  <Bot className="h-5 w-5 text-primary" />
                  <h2 className="text-lg font-semibold text-foreground">Histórico de Execuções</h2>
                  <span className="ml-2 hidden text-sm text-muted-foreground sm:inline-block">
                    Acompanhe os logs e payloads de integrações de inteligência artificial.
                  </span>
                </div>

                {/* Instanciando nossa tabela componentizada */}
                <ProcessamentoIAListTable storageKey="processamentos-ia-admin-v1" />
              </div>
            ),
          },
          {
            value: "estatisticas",
            label: "Estatísticas",
            icon: BarChart3,
            content: <ProcessamentoIAEstatisticasPanel />,
          },
        ]}
      />
    </div>
  );
}
